import barba from "@barba/core";
import gsap from "gsap";

import { killAllScrollTriggers, resetScrollPosition, stopScroll, startScroll, requestScrollTriggerRefresh, rebindJumpLinks, removeScrollElements, addScrollElements, checkInitialHash } from "./scroll";
import { calcResize } from "../utils/variables";
import { resetCursorState, recomputeCursorState } from "../components/cursor";
import { captureFromWorkDetail, captureFromNewsDetail, isWorkDetailNamespace, hasPending, getPendingType, animateWorkForward, animateNewsForward, animateWorkReverse, animateNewsReverse, dismissOverlay, cleanup as cleanupMediaTransition } from "../layout/work-video-transition";
import { resetMenuTrigger, hideMenuTrigger, closeMenu } from "../layout/menu";

const modalNamespaces = ["work-detail", "work-detail-music", "news-detail", "contact", "clients"];
const footerHiddenNamespaces = ["contact", "clients"];

let pageInitCallback: () => Promise<void> | void;
let previousUrl: string | null = null;
let backdropEl: HTMLElement | null = null;
let frozenContainerTop = 0;
let hasMediaTransition = false;

export function getPreviousUrl() {
    return previousUrl;
}

export function setPageInitCallback(fn: () => Promise<void> | void) {
    pageInitCallback = fn;
}

function createBackdrop(sourceContainer: HTMLElement) {
    backdropEl?.remove();

    backdropEl = sourceContainer.cloneNode(true) as HTMLElement;
    backdropEl.removeAttribute("data-barba");
    backdropEl.removeAttribute("data-barba-namespace");
    backdropEl.removeAttribute("style");
    backdropEl.classList.add("barba-backdrop");
    backdropEl.setAttribute("aria-hidden", "true");
    backdropEl.setAttribute("inert", "");
    backdropEl.style.top = `${frozenContainerTop}px`;

    const firstContainer = document.querySelector('[data-barba="container"]');
    if (firstContainer) {
        document.body.insertBefore(backdropEl, firstContainer);
    } else {
        document.body.appendChild(backdropEl);
    }
}

function removeBackdrop() {
    if (backdropEl) {
        backdropEl.remove();
        backdropEl = null;
    }

    document.body.classList.remove("has-modal-backdrop");
}

function freezeContainer(container: HTMLElement, zIndex: number) {
    frozenContainerTop = container.getBoundingClientRect().top;
    gsap.set(container, {
        position: "fixed",
        top: frozenContainerTop,
        left: 0,
        width: "100%",
        zIndex,
        pointerEvents: "none",
        overflow: "hidden",
    });
}

function updatePageTheme(container: HTMLElement) {
    const isDark = container.dataset.pageTheme === "dark";
    container.classList.toggle("page--dark", isDark);
    document.body.setAttribute("data-theme", isDark ? "dark" : "light");
}

const navBreakMq = window.matchMedia(`(max-width: ${1232 - 0.02}px)`);

function applyHeaderToggle(scope?: HTMLElement) {
    const root = scope ?? document.querySelector<HTMLElement>('[data-barba="container"]');
    if (!root) return;

    const minimal = root.querySelector<HTMLElement>('header[data-header="minimal"]');
    const full = root.querySelector<HTMLElement>('header[data-header="full"]');
    if (!minimal || !full) return;

    if (navBreakMq.matches) {
        full.hidden = true;
        minimal.hidden = false;
    } else {
        const hasSeenHeader = sessionStorage.getItem("hasSeenHeader") === "1";

        full.hidden = hasSeenHeader;
        minimal.hidden = !hasSeenHeader;

        if (!hasSeenHeader) sessionStorage.setItem("hasSeenHeader", "1");
    }
}

function updatePageTitle(html: string) {
    const match = html.match(/<title>([^<]*)<\/title>/i);
    if (match) document.title = match[1];
}

function closeMenuIfOpen() {
    const menu = document.querySelector<HTMLDialogElement>(".menu");
    if (menu?.open) {
        void closeMenu({ keepTriggerHidden: true });
    } else {
        hideMenuTrigger();
    }
}

function updateFooterVisibility(namespace: string) {
    const hideFooter = footerHiddenNamespaces.includes(namespace);
    const footer = document.querySelector<HTMLElement>(".footer");
    if (footer) footer.hidden = hideFooter;
}

const menuPageIdByNamespace: Record<string, string> = {
    "work-detail": "work",
    "work-detail-music": "work",
    "news-detail": "news",
};

function updateMenuActiveState(namespace: string) {
    const pageId = menuPageIdByNamespace[namespace] ?? namespace;

    document.querySelectorAll<HTMLAnchorElement>(".menu [data-page-id]").forEach(link => {
        link.classList.toggle("active", link.dataset.pageId === pageId);
    });
}

function applyPageState(container: HTMLElement, html: string) {
    const namespace = container.dataset.barbaNamespace || "";

    updatePageTheme(container);
    updateFooterVisibility(namespace);
    updateMenuActiveState(namespace);
    updatePageTitle(html);
    applyHeaderToggle(container);

    document.body.classList.toggle("is-home", namespace === "home");

    window.dispatchEvent(
        new CustomEvent("logo:page-update", {
            detail: {
                container,
                namespace,
            },
        })
    );
}

async function runPageInit() {
    resetCursorState();
    startScroll();
    calcResize(false);
    rebindJumpLinks();
    resetMenuTrigger();
    await pageInitCallback?.();
    requestScrollTriggerRefresh(100);
    checkInitialHash();
    recomputeCursorState();
}

export function initBarba() {
    navBreakMq.addEventListener("change", () => applyHeaderToggle());

    barba.init({
        preventRunning: true,
        timeout: 10000,

        transitions: [
            {
                name: "modal-to-modal",
                sync: true,
                from: { namespace: modalNamespaces },
                to: { namespace: modalNamespaces },

                beforeLeave(data) {
                    previousUrl = data.current.url.href;
                    closeMenuIfOpen();
                    resetCursorState();
                    killAllScrollTriggers();
                    removeScrollElements(data.current.container);
                    stopScroll();
                    gsap.set(data.current.container, { zIndex: 10 });
                },

                beforeEnter(data) {
                    applyPageState(data.next.container, data.next.html);
                    data.next.container.classList.add("barba-modal");
                    gsap.set(data.next.container, { opacity: 0, zIndex: 5 });
                },

                leave(data) {
                    return gsap.to(data.current.container, {
                        opacity: 0,
                        duration: 0.4,
                        ease: "power2.inOut",
                    });
                },

                enter(data) {
                    return gsap.to(data.next.container, {
                        opacity: 1,
                        duration: 0.4,
                        ease: "power2.inOut",
                    });
                },

                async afterEnter(data) {
                    window.scrollTo(0, 0);
                    resetScrollPosition();
                    addScrollElements(data.next.container);
                    await runPageInit();
                },
            },

            {
                name: "page-to-modal",
                sync: true,
                to: { namespace: modalNamespaces },

                beforeLeave(data) {
                    previousUrl = data.current.url.href;
                    hasMediaTransition = hasPending();
                    closeMenuIfOpen();
                    resetCursorState();
                    killAllScrollTriggers();
                    removeScrollElements(data.current.container);
                    stopScroll();

                    freezeContainer(data.current.container, 1);
                },

                async beforeEnter(data) {
                    window.scrollTo(0, 0);
                    document.body.classList.add("has-modal-backdrop");

                    applyPageState(data.next.container, data.next.html);

                    data.next.container.classList.add("barba-modal");
                    gsap.set(data.next.container, { opacity: 0 });

                    if (hasMediaTransition) {
                        const type = getPendingType();
                        if (type === "video") await animateWorkForward(data.next.container);
                        else if (type === "image") await animateNewsForward(data.next.container);
                    }
                },

                leave() {},

                enter(data) {
                    return gsap.to(data.next.container, {
                        opacity: 1,
                        duration: 0.4,
                        ease: "power2.inOut",
                    });
                },

                async afterEnter(data) {
                    createBackdrop(data.current.container);
                    resetScrollPosition();
                    addScrollElements(data.next.container);
                    await dismissOverlay();
                    await runPageInit();
                },
            },

            {
                name: "modal-to-page",
                sync: true,
                from: { namespace: modalNamespaces },

                beforeLeave(data) {
                    previousUrl = data.current.url.href;
                    closeMenuIfOpen();
                    resetCursorState();
                    killAllScrollTriggers();
                    removeScrollElements(data.current.container);
                    stopScroll();

                    if (isWorkDetailNamespace(data.current.namespace) && data.next.namespace === "work") {
                        captureFromWorkDetail(data.current.container);
                    } else if (data.current.namespace === "news-detail" && data.next.namespace === "news") {
                        captureFromNewsDetail(data.current.container);
                    }

                    freezeContainer(data.current.container, 10);
                },

                beforeEnter(data) {
                    removeBackdrop();
                    window.scrollTo(0, 0);
                    resetScrollPosition();
                    applyPageState(data.next.container, data.next.html);
                },

                leave(data) {
                    return gsap.to(data.current.container, {
                        opacity: 0,
                        duration: 0.4,
                        ease: "power2.inOut",
                    });
                },

                enter(data) {
                    let mediaPromise: Promise<void> | undefined;
                    if (hasPending()) {
                        const type = getPendingType();

                        if (type === "video") mediaPromise = animateWorkReverse(data.next.container);
                        else if (type === "image") mediaPromise = animateNewsReverse(data.next.container);
                    }

                    return mediaPromise || Promise.resolve();
                },

                async afterEnter(data) {
                    cleanupMediaTransition();
                    addScrollElements(data.next.container);
                    await runPageInit();
                },
            },

            {
                name: "default",
                sync: true,

                beforeLeave(data) {
                    previousUrl = data.current.url.href;
                    closeMenuIfOpen();
                    resetCursorState();
                    killAllScrollTriggers();
                    removeScrollElements(data.current.container);
                    stopScroll();
                    freezeContainer(data.current.container, 10);
                },

                beforeEnter(data) {
                    window.scrollTo(0, 0);
                    resetScrollPosition();
                    removeBackdrop();
                    applyPageState(data.next.container, data.next.html);
                },

                leave(data) {
                    return gsap.to(data.current.container, {
                        opacity: 0,
                        duration: 0.4,
                        ease: "power2.inOut",
                    });
                },

                enter() {},

                async afterEnter(data) {
                    addScrollElements(data.next.container);
                    await runPageInit();
                },
            },
        ],
    });
}
